import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { BankAccountSection } from "@/components/shared/BankAccountSection";
import { PayoutStatusBadge } from "@/components/shared/PayoutStatusBadge";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { statusBadgeVariant } from "@/lib/professionalPayoutStatus";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatUyMinor } from "@/lib/formatUyMinor";
import {
  deleteProfessionalBankAccount,
  getProfessionalBankAccount,
  getProfessionalPayoutSummary,
  listProfessionalPayoutLines,
  listProfessionalPayoutRequests,
  putProfessionalBankAccount,
  type ProfessionalBankAccountDto,
  type ProfessionalPayoutRequestRow,
} from "@/services/professionalEarningsService";
import { useTotpStepUp } from "@/components/features/security/use-totp-step-up";

export function ProfessionalEarningsPage(): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "professionalEarnings" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pagination = usePaginationFromUrl();

  const summaryQuery = useQuery({
    queryKey: ["me", "professional", "payouts", "summary"],
    queryFn: getProfessionalPayoutSummary,
  });

  const linesQuery = useQuery({
    queryKey: ["me", "professional", "payouts", "lines", pagination.page, pagination.limit],
    queryFn: () =>
      listProfessionalPayoutLines({
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      }),
  });

  const bankQuery = useQuery({
    queryKey: ["me", "professional", "bank-account"],
    queryFn: getProfessionalBankAccount,
  });

  const payoutRequestsQuery = useQuery({
    queryKey: ["me", "professional", "payouts", "requests", "recent"],
    queryFn: () => listProfessionalPayoutRequests({ limit: 5, offset: 0 }),
  });

  // Changing or removing bank details is a step-up action: the backend
  // refuses it when this session's last code is older than the grace window,
  // and `guard` answers that over the form rather than navigating away from
  // half-entered account numbers.
  const { guard: stepUpGuard, dialog: stepUpDialog } = useTotpStepUp();

  const saveBankMutation = useMutation({
    mutationFn: (body: ProfessionalBankAccountDto) =>
      stepUpGuard(() => putProfessionalBankAccount(body)),
    onSuccess: () => {
      toast.success(t("bankSaveSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["me", "professional", "bank-account"] });
    },
    onError: () => {
      toast.error(t("bankSaveError"));
    },
  });

  const deleteBankMutation = useMutation({
    mutationFn: () => stepUpGuard(() => deleteProfessionalBankAccount()),
    onSuccess: () => {
      toast.success("Cuenta bancaria eliminada");
      void queryClient.invalidateQueries({ queryKey: ["me", "professional", "bank-account"] });
    },
    onError: () => {
      toast.error("No se pudo eliminar la cuenta bancaria");
    },
  });

  const summary = summaryQuery.data;
  const lines = linesQuery.data?.items ?? [];
  const totalLines = linesQuery.data?.total ?? 0;

  useEffect(() => {
    if (linesQuery.data != null) {
      pagination.setTotal(linesQuery.data.total);
    } else if (linesQuery.isError) {
      pagination.setTotal(0);
    }
  }, [linesQuery.data, linesQuery.isError, pagination]);

  useEffect(() => {
    if (summaryQuery.isError) {
      toast.error(t("loadError"));
    }
  }, [summaryQuery.isError, t]);

  useEffect(() => {
    if (linesQuery.isError) {
      toast.error(t("linesLoadError"));
    }
  }, [linesQuery.isError, t]);

  if (summaryQuery.isLoading && !summaryQuery.data) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 md:p-8">
        <PageHeader title={t("pageTitle")} description={t("pageSubtitle")} />

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Card className="transition-shadow duration-200 hover:shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("summaryTotalEarned")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-foreground">
                {summary
                  ? formatUyMinor(summary.totalEarnedMinor, summary.currency)
                  : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="transition-shadow duration-200 hover:shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("summaryPending")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-foreground">
                {summary
                  ? formatUyMinor(summary.pendingPaymentMinor, summary.currency)
                  : "—"}
              </p>
            </CardContent>
          </Card>
          <Card className="transition-shadow duration-200 hover:shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("summaryPaid")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-foreground">
                {summary ? formatUyMinor(summary.paidOutMinor, summary.currency) : "—"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-foreground">{t("bankCardTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <BankAccountSection
              account={bankQuery.data}
              isLoading={bankQuery.isLoading}
              isSubmitting={saveBankMutation.isPending}
              isDeleting={deleteBankMutation.isPending}
              onSubmit={(body) => saveBankMutation.mutateAsync(body)}
              onDelete={() => deleteBankMutation.mutateAsync()}
            />
          </CardContent>
        </Card>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {bankQuery.data
              ? t("payMeHint")
              : "Configurá tu cuenta bancaria antes de solicitar un pago."}
          </p>
          <Button
            type="button"
            onClick={() => navigate("/earnings/request-payout")}
            disabled={!bankQuery.data}
            className="transition-colors duration-150"
          >
            {t("payMe")}
          </Button>
        </div>

        <PayoutRequestsHistoryCard
          items={payoutRequestsQuery.data?.items ?? []}
          isLoading={payoutRequestsQuery.isLoading}
        />

        <TableCard className="mt-4">
          {linesQuery.isLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <LoadingLogo size="lg" />
            </div>
          ) : lines.length === 0 ? (
            <div className="flex min-h-[40vh] items-center justify-center p-6">
              <EmptyState title={t("linesEmptyTitle")} message={t("linesEmptyMessage")} />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("linesColumnRequest")}</TableHead>
                    <TableHead>{t("linesColumnAmount")}</TableHead>
                    <TableHead>{t("linesColumnPayoutStatus")}</TableHead>
                    <TableHead>{t("linesColumnDate")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => {
                    const requestId = line.notarialRequestId ?? line.accountantRequestId;
                    const openDetail = () => {
                      if (!requestId) return;
                      navigate(`/requests/${requestId}`, {
                        state: { from: "/earnings" },
                      });
                    };
                    return (
                      <TableRow
                        key={line.id}
                        onClick={requestId ? openDetail : undefined}
                        onKeyDown={(e) => {
                          if (!requestId) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDetail();
                          }
                        }}
                        tabIndex={requestId ? 0 : undefined}
                        role={requestId ? "link" : undefined}
                        className={
                          requestId
                            ? "cursor-pointer transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                            : undefined
                        }
                      >
                        <TableCell>
                          {line.requestLabel || line.requesterName ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">
                                {line.requestLabel ?? "Solicitud"}
                              </span>
                              {line.requesterName ? (
                                <span className="text-xs text-muted-foreground">
                                  {line.requesterName}
                                </span>
                              ) : null}
                            </div>
                          ) : requestId ? (
                            <span className="font-medium text-primary">
                              {requestId.slice(0, 8)}…
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatUyMinor(line.amountMinor, line.currency)}
                        </TableCell>
                        <TableCell>
                          <PayoutStatusBadge
                            status={line.payoutStatus}
                            labels={{
                              paid: t("linePayoutPaid"),
                              pending: t("linePayoutPendingReview"),
                              notRequested: t("linePayoutNotRequested"),
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(line.createdAt).toLocaleDateString("es-UY")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("rowsPerPage")}
                pageOfLabel={t("pageOf", {
                  from: totalLines === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, totalLines),
                  total: totalLines,
                })}
                onRefresh={() => void linesQuery.refetch()}
                isRefreshing={linesQuery.isFetching}
              />
            </>
          )}
        </TableCard>
      </div>
      {/* MUST be rendered: `guard` awaits a promise this dialog
          settles, so without it a step-up refusal would hang the
          action forever instead of asking for a code. */}
      {stepUpDialog}
    </>
  );
}

function PayoutRequestsHistoryCard({
  items,
  isLoading,
}: {
  items: ProfessionalPayoutRequestRow[];
  isLoading: boolean;
}): JSX.Element | null {
  const { t } = useTranslation(undefined, { keyPrefix: "professionalEarnings" });
  if (isLoading && items.length === 0) return null;
  if (items.length === 0) return null;

  const statusKeyMap: Record<ProfessionalPayoutRequestRow["status"], string> = {
    pending: "payoutsStatusPending",
    processing: "payoutsStatusProcessing",
    paid: "payoutsStatusPaid",
    cancelled: "payoutsStatusCancelled",
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-foreground">{t("payoutsHistoryTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("linesColumnRequest")}</TableHead>
              <TableHead>{t("linesColumnAmount")}</TableHead>
              <TableHead>{t("linesColumnPayoutStatus")}</TableHead>
              <TableHead>{t("linesColumnDate")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => {
              const lastAt =
                row.paidAt ?? row.cancelledAt ?? row.processingAt ?? row.createdAt;
              return (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {row.id.slice(0, 8)}…
                  </TableCell>
                  <TableCell className="font-medium text-foreground">
                    {formatUyMinor(row.totalMinor, row.currency)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge variant={statusBadgeVariant(row.status)}>
                      {t(statusKeyMap[row.status])}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(lastAt).toLocaleDateString("es-UY")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
